import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as dates from '@simplybenefits/sb-dates';
import {
    Button,
    Text,
    Container,
    ContentSmall,
    Title,
    Theme,
    i18n,
    tokens,
    TrusteeModal
} from '@simplybenefits/sb-ui-library';

import PersonBlock from './PersonBlock.js';
import { addTrustee, editTrustee, removeTrustee } from '../../actions/user.js';

/**
 * Trustee Component
 *
 * @param {Object} props - Component properties
 * @returns {JSX.Element} - Rendered component
 */
const Trustee = ({
    primary, 
    contingent,
    dispatch,
    trustee
}) => {
    const [ isEdit, setIsEdit ] = useState(false);
    const [ isRemoveTrustee, setIsRemoveTrustee ] = useState(false);
    const [ showAddTrusteeModal, setShowAddTrusteeModal ] = useState(false);
    const [ skipTrustee, setSkipTrustee ] = useState(false);

    /**
     * Calculates age based on birthdate
     *
     * @param {string} birthdate - The birthdate of the individual
     * @returns {number} - The calculated age
     */
    const getAge = (birthdate) => {
        birthdate = dates.getUTC(birthdate);
        return Math.floor((dates.getUTC() - birthdate) / (60 * 60 * 24 * 365));
    };

    useEffect(() => {

        /**
         * Determines if trustee can be skipped based on beneficiaries' ages
         */
        const determineSkipTrustee = () => {
            const beneficiaries = [ ...primary, ...contingent ];
    
            beneficiaries.length && beneficiaries.map((beneficiary) => {
                const age = getAge(beneficiary.birthdate * 1000);
    
                if (age >= 18) {
                    setSkipTrustee(true);
                } else if (age < 18) {
                    setSkipTrustee(false);
                }
    
                return null;
            });
        };

        determineSkipTrustee();
    }, []);
    
    const styles = {
        signature: {
            fontFamily: 'AntaraDistance',
            fontWeight: 'bold',
            fontSize: 20,
            padding: 0
        },
        titleBlock: {
            display: 'flex',
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: "space-between"
        },
        iconStyle: {
            padding: "12px",
            color: "#414042",
            '&.MuiButtonBase-root:hover': {
                backgroundColor: Theme.colors.hoverGrey
            }
        }
    };

    /**
     * Shows the Add Trustee modal
     */
    const showAddTrustee = () => {
        setShowAddTrusteeModal(true);
    };

    /**
     * Shows the Edit Trustee modal
     */
    const showEditTrustee = () => {
        setIsEdit(true);
        setShowAddTrusteeModal(true);
    };

    /**
     * Shows the Remove Trustee modal
     */
    const showRemoveTrustee = () => {
        setShowAddTrusteeModal(true);
        setIsRemoveTrustee(true);
    };

    /**
     * 
     */
    const closeTrusteeModal = () => {
        setIsRemoveTrustee(false);
        setShowAddTrusteeModal(false);
        setIsEdit(false);
    };

    return (
        <Container>
            <ContentSmall style={{ width: '60%' }}>
                <div style={ styles.titleBlock }>
                    <Title title={ i18n.t(tokens.titles.trustee) } containerStyle={{ flex: 1 }} />

                    {
                        (!trustee || trustee.firstname === null) && !skipTrustee &&
                        <Button
                            color="primary"
                            customStyle={{
                                height: 50,
                                width: 180,
                                marginRight: 1.25,
                                textTransform: "capitalize",
                                fontSize: 16
                            }}
                            onClick={ showAddTrustee }
                        >
                            { i18n.t(tokens.buttons["add trustee"])}
                        </Button>
                    }
                </div>

                {
                    (!trustee || trustee.firstname === null) ?
                        skipTrustee ?
                            <Text
                                testid="trustee"
                                light
                                medium
                                align="center"
                            >
                                { i18n.t(tokens.labels.account.noTrusteeRequired)}
                            </Text>
                            :
                            <Text
                                testid="trustee"
                                light
                                medium
                                align="center"
                            >
                                { i18n.t(tokens.labels.account.addTrustee) }
                            </Text>
                        :
                        <PersonBlock
                            testid="trustee"
                            key={trustee.trusteeID}
                            firstname={trustee.firstname}
                            lastname={trustee.lastname}
                            relationship={trustee.relationship}
                            dateAdded={trustee.dateAdded}
                            parentID={trustee.parentID}
                            type="trustee"
                            edit={() => showEditTrustee()}
                            remove={() => showRemoveTrustee()}
                            iconCustomStyle={styles.iconStyle}
                        />
                }

                <TrusteeModal
                    open={showAddTrusteeModal}
                    isEdit={isEdit}
                    onClose={ closeTrusteeModal }
                    isRemoveTrustee={isRemoveTrustee}
                    trusteeData={trustee}
                    addTrustee={(...params) => dispatch(addTrustee(...params))}
                    editTrustee={(...params) => dispatch(editTrustee(...params))}
                    removeTrustee={(...params) => dispatch(removeTrustee(...params))}
                    isAdmin={false}
                />
            </ContentSmall>
        </Container>
    );
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    trustee: state.user.trustee,
    primary: state.user.beneficiaries.primary,
    contingent: state.user.beneficiaries.contingent,
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(Trustee);